/**
 * Modelo de período para los reportes de utilidades.
 *
 * Hay 4 modos visibles para el usuario:
 *  - `mes`       : un mes específico (default histórico, equivalente al
 *                  selector original de "anio + mes")
 *  - `trimestre` : un trimestre Q1/Q2/Q3/Q4 de un año (3 meses agregados)
 *  - `anio`      : los 12 meses de un año (agregados)
 *  - `rango`     : rango libre de meses (desde mes/año hasta mes/año)
 *
 * Todos los modos se normalizan internamente a un rango cerrado de
 * (anio, mes) ↔ (anio, mes) que el backend consume. El campo `modo` solo
 * existe para la UI; el backend recibe únicamente el rango ya resuelto
 * via `obtenerRangoPeriodo()`.
 */
/**
 * Normaliza cualquier `Periodo` a un rango cerrado de meses.
 * Es la única forma en que el backend interpreta el período del usuario.
 */
export function obtenerRangoPeriodo(p) {
    switch (p.modo) {
        case 'mes':
            return {
                desdeAnio: p.anio,
                desdeMes: p.mes,
                hastaAnio: p.anio,
                hastaMes: p.mes,
            };
        case 'trimestre': {
            const q = p.trimestre ?? 1;
            const desdeMes = (q - 1) * 3 + 1;
            return {
                desdeAnio: p.anio,
                desdeMes,
                hastaAnio: p.anio,
                hastaMes: desdeMes + 2,
            };
        }
        case 'anio':
            return {
                desdeAnio: p.anio,
                desdeMes: 1,
                hastaAnio: p.anio,
                hastaMes: 12,
            };
        case 'rango':
            return {
                desdeAnio: p.desdeAnio ?? p.anio,
                desdeMes: p.desdeMes ?? p.mes,
                hastaAnio: p.hastaAnio ?? p.anio,
                hastaMes: p.hastaMes ?? p.mes,
            };
    }
}
/**
 * Mes "ancla" para entidades que siguen siendo por-mes (ej. comentarios).
 * Convención: el último mes del rango. Si el usuario está viendo
 * "Ene–May 2026", los comentarios que ve/agrega van anclados a May 2026.
 */
export function mesAnclaPeriodo(p) {
    const r = obtenerRangoPeriodo(p);
    return { anio: r.hastaAnio, mes: r.hastaMes };
}
/** True si el período cubre un solo mes (ya sea modo 'mes' o un rango de 1 mes). */
export function periodoEsMesUnico(p) {
    const r = obtenerRangoPeriodo(p);
    return r.desdeAnio === r.hastaAnio && r.desdeMes === r.hastaMes;
}
const NOMBRES_MES_CORTO = [
    'Ene',
    'Feb',
    'Mar',
    'Abr',
    'May',
    'Jun',
    'Jul',
    'Ago',
    'Sep',
    'Oct',
    'Nov',
    'Dic',
];
/** Etiqueta para mostrar en encabezados / títulos / nombres de archivo. */
export function formatearPeriodo(p) {
    switch (p.modo) {
        case 'mes':
            return `${NOMBRES_MES_CORTO[p.mes - 1]} ${p.anio}`;
        case 'trimestre':
            return `Q${p.trimestre ?? 1} ${p.anio}`;
        case 'anio':
            return `Año ${p.anio}`;
        case 'rango': {
            const r = obtenerRangoPeriodo(p);
            const desde = `${NOMBRES_MES_CORTO[r.desdeMes - 1]} ${r.desdeAnio}`;
            const hasta = `${NOMBRES_MES_CORTO[r.hastaMes - 1]} ${r.hastaAnio}`;
            if (r.desdeAnio === r.hastaAnio && r.desdeMes === r.hastaMes)
                return desde;
            if (r.desdeAnio === r.hastaAnio) {
                return `${NOMBRES_MES_CORTO[r.desdeMes - 1]}–${NOMBRES_MES_CORTO[r.hastaMes - 1]} ${r.desdeAnio}`;
            }
            return `${desde} – ${hasta}`;
        }
    }
}
//# sourceMappingURL=periodo.js.map